import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Compact multi-select dropdown primitive (docs/UI_STANDARDS — one dropdown
 * pattern, reused). A single trigger collapses a many-option facet to one slot so
 * the cash-flow filter fits in ≤ 2 rows; the panel opens on click and closes via a
 * full-screen backdrop (the same overlay pattern as the period popover — no ref /
 * document-listener branch, which keeps the file fully covered). Controlled: it
 * owns only its open state; the selection is lifted to the caller (master rule:
 * shared filter state, not per-component).
 */
export interface MultiSelectOption {
  value: string;
  label: string;
  /** Live tally from the current window (defaults to 0 when absent). */
  count?: number;
  /** Optional colour dot (income green / cost red) — mirrors the facet meta. */
  dotKind?: 'income' | 'cost';
}

export interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}

export function MultiSelect({ label, options, selected, onToggle }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const n = selected.size;
  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-label={n ? `${label} (${n})` : label}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition-colors',
          n
            ? 'border-primary bg-primary/15 text-foreground'
            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
      >
        <span>{label}</span>
        {n > 0 && (
          <span className="rounded-full bg-primary/30 px-1 text-[10px] tabular-nums text-foreground">{n}</span>
        )}
        <span aria-hidden className="text-[8px] opacity-60">
          ▾
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
          <div
            role="listbox"
            aria-label={label}
            className="absolute left-0 top-full z-50 mt-1 max-h-64 w-52 overflow-auto rounded-md border border-border bg-card p-1 shadow-xl"
          >
            {options.length === 0 ? (
              <div className="px-2 py-1 text-[11px] text-muted-foreground">—</div>
            ) : (
              options.map((o) => {
                const on = selected.has(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={on}
                    aria-label={`${label}: ${o.label} (${o.count ?? 0})`}
                    onClick={() => onToggle(o.value)}
                    className={cn(
                      'flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] transition-colors',
                      on
                        ? 'bg-primary/15 text-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'inline-flex h-3 w-3 items-center justify-center rounded-[3px] border text-[9px]',
                        on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                      )}
                    >
                      {on ? '✓' : ''}
                    </span>
                    {o.dotKind && (
                      <span
                        aria-hidden
                        className={cn(
                          'inline-block h-2 w-2 shrink-0 rounded-[2px]',
                          o.dotKind === 'income' ? 'bg-[hsl(var(--income))]' : 'bg-[hsl(var(--cost))]',
                        )}
                      />
                    )}
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    <span className="tabular-nums text-[10px] text-muted-foreground">{o.count ?? 0}</span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
